import axios from 'axios'
import {
  CAPTCHA_MODES,
  CAPTCHA_PROVIDERS,
  CAPTCHA_REQUIRED_CODE,
  CAPTCHA_VERIFICATION_FAILED_CODE,
} from 'helpers/captcha/contracts'
import type {
  CaptchaAxiosError,
  CaptchaChallenge,
} from 'helpers/captcha/contracts'

type CaptchaPayload = {
  captcha_provider?: unknown
  captcha_mode?: unknown
  captcha_site_key?: unknown
}

function getChallengePayload(
  error: CaptchaAxiosError,
): CaptchaPayload | null {
  const body = error.response?.data
  if (body === null || typeof body !== 'object') return null

  const data = 'data' in body ? body.data : undefined
  return data !== null && typeof data === 'object' ? data : body
}

function hasErrorCode(
  error: unknown,
  status: number,
  code: string,
): boolean {
  if (!axios.isAxiosError(error)) return false

  const body = error.response?.data
  return (
    error.response?.status === status &&
    body !== null &&
    typeof body === 'object' &&
    'code' in body &&
    body.code === code
  )
}

export function isCaptchaRequiredError(
  error: unknown,
): error is CaptchaAxiosError {
  return hasErrorCode(error, 403, CAPTCHA_REQUIRED_CODE)
}

/** The backend refused the CAPTCHA token the request carried. */
export function isCaptchaVerificationRejectedError(
  error: unknown,
): boolean {
  return hasErrorCode(error, 400, CAPTCHA_VERIFICATION_FAILED_CODE)
}

/**
 * Reads the challenge off a CAPTCHA_REQUIRED error, from `data` or from the
 * body itself. Null when the error is not one or names a provider or mode
 * this client does not know.
 *
 * @param fallbackSiteKey - Used when the challenge carries no site key.
 */
export function parseCaptchaChallenge(
  error: unknown,
  fallbackSiteKey: string,
): CaptchaChallenge | null {
  try {
    if (!isCaptchaRequiredError(error)) return null

    const payload = getChallengePayload(error)
    if (!payload) return null

    const provider = CAPTCHA_PROVIDERS.find(
      (candidate) => candidate === payload.captcha_provider,
    )
    const mode = CAPTCHA_MODES.find(
      (candidate) => candidate === payload.captcha_mode,
    )
    if (!provider || !mode) return null

    const siteKey =
      typeof payload.captcha_site_key === 'string' &&
      payload.captcha_site_key.trim() !== ''
        ? payload.captcha_site_key.trim()
        : fallbackSiteKey

    return { provider, mode, siteKey }
  } catch {
    return null
  }
}
